import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  registerUser,
  clearError,
  requestSmsOtp,
  resendSmsOtp,
  verifyEmailOtp,
  verifyPhoneOtp,
} from "@/store/slices/authSlice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    secondary_mobile_no: "",
    password: "",
    password_confirmation: "",
    organization_type: "",
    other_organization: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [emailOtp, setEmailOtp] = useState(Array(6).fill(""));
  const [phoneOtp, setPhoneOtp] = useState(Array(6).fill(""));
  const [showEmailOtpBox, setShowEmailOtpBox] = useState(false);
  const [showPhoneOtpBox, setShowPhoneOtpBox] = useState(false);
  const [otpVerified, setOtpVerified] = useState({
    email: false,
    phone: false,
  });
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneTimer, setPhoneTimer] = useState(0);
  const [registrationSuccess, setRegistrationSuccess] = useState(false); // Changed from successMessage

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, registerError } = useSelector((state) => state.auth);
  const [openModal, setOpenModal] = useState(false);
  const nameRef = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();
  const secondaryPhoneRef = useRef();
  const passwordRef = useRef();
  const confirmPasswordRef = useRef();
  const orgTypeRef = useRef();
  const otherOrgRef = useRef();

  const inputRefs = {
    name: nameRef,
    email: emailRef,
    phone: phoneRef,
    secondary_mobile_no: secondaryPhoneRef,
    password: passwordRef,
    password_confirmation: confirmPasswordRef,
    organization_type: orgTypeRef,
    other_organization: otherOrgRef,
  };
  const [passwordPolicy, setPasswordPolicy] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const phoneOtpRefs = useRef([]);

  // Handle validation errors
  useEffect(() => {
    if (registerError) {
      if (registerError.includes("password confirmation"))
        setValidationErrors({ password_confirmation: registerError });
      else if (registerError.includes("email"))
        setValidationErrors({ email: registerError });
      else if (registerError.includes("phone"))
        setValidationErrors({ phone: registerError });
      else if (registerError.includes("secondary_mobile_no"))
        setValidationErrors({ secondary_mobile_no: registerError });
      else if (registerError.includes("other_organization"))
        setValidationErrors({ other_organization: registerError });
      else if (registerError.includes("name"))
        setValidationErrors({ name: registerError });
      else setValidationErrors({ general: registerError });
    } else {
      setValidationErrors({});
    }
  }, [registerError]);

  // Phone timer countdown
  useEffect(() => {
    let interval;
    if (phoneTimer > 0) {
      interval = setInterval(() => {
        setPhoneTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [phoneTimer]);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,10}$/.test(value)) setFormData({ ...formData, phone: value });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Full Name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.phone) errors.phone = "Primary Mobile number is required";
    if (!formData.secondary_mobile_no)
      errors.secondary_mobile_no = "Secondary Mobile number is required";
    else if (!/^\d{10}$/.test(formData.phone))
      errors.phone = "Phone number must be exactly 10 digits";
    if (!formData.password) {
      errors.password = "Password is required";
    } else {
      const policy = validatePasswordPolicy(formData.password);
      if (
        !policy.length ||
        !policy.uppercase ||
        !policy.lowercase ||
        !policy.number ||
        !policy.special
      ) {
        errors.password =
          "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
      }
    }
    if (!formData.password_confirmation)
      errors.password_confirmation = "Confirm password is required";
    else if (formData.password !== formData.password_confirmation)
      errors.password_confirmation = "Password confirmation does not match";
    if (!formData.organization_type)
      errors.organization_type = "Organization type is required";
    if (formData.organization_type == "others" && !formData.other_organization)
      errors.other_organization = "Other Organization field is required";
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    setValidationErrors({});
    setRegistrationSuccess(false); // Reset success state

    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      inputRefs[firstErrorField]?.current?.focus();
      return;
    }

    try {
      const res = await dispatch(registerUser(formData)).unwrap();
      setRegistrationSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        secondary_mobile_no: "",
        password: "",
        password_confirmation: "",
        organization_type: "",
        other_organization: "",
      });

      toast({
        title: "Saved",
        description: "Registration Successfully",
        variant: "success",
      });
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setValidationErrors({ general: err?.message || "Registration failed" });
      setRegistrationSuccess(false);
    }
  };

  const handleBlur = (field) => {
    const errors = validateForm();
    setValidationErrors((prev) => ({ ...prev, [field]: errors[field] }));
  };

  // Request OTP
  const handleGetOtp = async (type) => {
    try {
      if (
        type === "phone" &&
        formData.phone.length === 10 &&
        phoneTimer === 0
      ) {
        const res = await dispatch(
          requestSmsOtp({ phone: formData.phone, email: formData.email })
        ).unwrap();

        if (res?.status === 200 || res?.status === 201) {
          setPhoneOtpSent(true);
          setShowPhoneOtpBox(true);
          setPhoneOtp(Array(6).fill(""));
          setPhoneTimer(60);
          phoneOtpRefs.current[0]?.focus();
          // setValidationErrors({
          //   phone: res?.message,
          // });
        } else {
          setShowPhoneOtpBox(false);
          setPhoneOtpSent(false);
          setValidationErrors({
            phone: res?.errors?.phone || "",
            email: res?.errors?.email || "",
          });
        }
      }
    } catch (err) {
      setShowPhoneOtpBox(false);
      setPhoneOtpSent(false);
      setValidationErrors({
        phone: err?.errors?.phone || "",
        email: err?.errors?.email || "",
      });
      toast({
        title: "Something Went Wrong",
        description: err?.message,
        variant: "destructive",
      });
    }
  };

  // OTP input change
  const handleOtpChange = (type, index, value) => {
    if (!/^\d*$/.test(value)) return;
    const otpArray = type === "email" ? [...emailOtp] : [...phoneOtp];
    otpArray[index] = value;
    type === "email" ? setEmailOtp(otpArray) : setPhoneOtp(otpArray);

    if (value && index < 5) {
      const nextRef =
        type === "email"
          ? emailOtpRefs.current[index + 1]
          : phoneOtpRefs.current[index + 1];
      nextRef?.focus();
    }
    if (!value && index > 0) {
      const prevRef =
        type === "email"
          ? emailOtpRefs.current[index - 1]
          : phoneOtpRefs.current[index - 1];
      prevRef?.focus();
    }
  };

  // Verify OTP
  const handleVerifyOtp = async (type) => {
    const code = (type == "email" ? emailOtp : phoneOtp).join("");
    if (code.length !== 6) {
      setValidationErrors({ general: "OTP must be 6 digits" });
      toast({
        title: "Error",
        description: "OTP must be 6 digits",
        variant: "destructive", // usually used for errors
      });
      return;
    }

    let payload;
    if (type === "email") payload = { email: formData.email, otp: code };
    else payload = { phone: formData.phone, otp: code };

    const res =
      type === "email"
        ? await dispatch(verifyEmailOtp(payload))
        : await dispatch(verifyPhoneOtp(payload));

    if (res.meta.requestStatus === "fulfilled") {
      setOtpVerified((prev) => ({ ...prev, [type]: true }));
      toast({
        title: "Success",
        description:
          res?.payload?.message ||
          `${type === "email" ? "Email" : "Phone"} OTP verified successfully!`,
        variant: "success",
      });
    } else {
      toast({
        title: "Error",
        description: res?.error?.message || "OTP verification failed",
        variant: "destructive",
      });
    }
  };

  // Resend OTP
  const handleResendPhoneOtp = async (type) => {
    try {
      if (type === "phone" && phoneTimer === 0) {
        const res = await dispatch(resendSmsOtp(formData.phone));

        if (res.meta.requestStatus === "fulfilled") {
          setPhoneOtp(Array(6).fill(""));
          setPhoneTimer(60);
          phoneOtpRefs.current[0]?.focus();

          toast({
            title: "Success",
            description:
              res?.payload?.message || "Mobile no OTP resent successfully.",
            variant: "success",
          });
        } else {
          toast({
            title: "Failed",
            description:
              res?.payload?.message ||
              "Failed to resend OTP. Please try again.",
            variant: "destructive",
          });
        }
      }
    } catch (err) {
      console.error("Error resending OTP:", err);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again later.",
        variant: "destructive",
      });
    }
  };

  const renderOtpBoxes = (type) => {
    const otp = type === "email" ? emailOtp : phoneOtp;
    const refs = type === "email" ? emailOtpRefs : phoneOtpRefs;

    return (
      <div className="space-y-2 mt-2">
        <div className="flex items-center space-x-1">
          {otp.map((digit, i) => (
            <Input
              key={i}
              type="text"
              maxLength={1}
              value={digit}
              ref={(el) => (refs.current[i] = el)}
              onChange={(e) => handleOtpChange(type, i, e.target.value)}
              className="w-10 text-center"
            />
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => handleVerifyOtp(type)}
            disabled={otp.join("").length !== 6}
          >
            Verify OTP
          </Button>
        </div>
      </div>
    );
  };

  const validatePasswordPolicy = (password) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*]/.test(password),
    };
  };

  return (
    <div className="space-y-4">
      {/* Error Alert */}
      {validationErrors.general && (
        <Alert variant="destructive">
          <AlertDescription>{validationErrors.general}</AlertDescription>
        </Alert>
      )}

      {/* Success Alert - Only show when registration is successful */}
      {registrationSuccess && (
        <Alert variant="success" className="border-green-500 bg-green-100">
          <AlertDescription className="text-green-700">
            Account created successfully! Redirecting to Login....
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col gap-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Name of the Applicant / Name of the Organization{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              onBlur={() => handleBlur("name")}
              className={`w-full border px-3 py-2 ${
                validationErrors.name ? "border-red-500" : "border-gray-300"
              }`}
            />
            {validationErrors.name && (
              <p className="text-red-500 text-sm">{validationErrors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email id <span style={{ color: "red" }}>*</span>
            </Label>
            <div className="flex space-x-2">
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  setValidationErrors((prev) => ({ ...prev, email: null }));
                }}
                className={validationErrors.email ? "border-red-500" : ""}
              />
            </div>
            {validationErrors.email && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.email}
              </p>
            )}
            {showEmailOtpBox && !otpVerified.email && renderOtpBoxes("email")}
          </div>

          <div className="flex flex-wrap lg:flex-nowrap justify-between gap-4">
            {/* Phone */}
            <div className="w-full space-y-2 relative">
              <Label htmlFor="phone">
                Primary Mobile Number <span style={{ color: "red" }}>*</span>
              </Label>
              <div className="flex space-x-2 relative">
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onBlur={() => handleBlur("phone")}
                  className={`w-full pr-10 border px-3 py-2 ${
                    validationErrors.phone
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {otpVerified.phone && (
                  <span
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500"
                    style={{ fontWeight: "bold" }}
                  >
                    &#10003;
                  </span>
                )}

                {!otpVerified.phone && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      phoneOtpSent
                        ? handleResendPhoneOtp("phone")
                        : handleGetOtp("phone");
                    }}
                    disabled={
                      phoneTimer > 0 || !formData.phone || !formData.email
                    }
                  >
                    {phoneTimer > 0
                      ? `Resend in ${phoneTimer}s`
                      : phoneOtpSent
                      ? "Resend Phone OTP"
                      : "Get OTP"}
                  </Button>
                )}
              </div>
              {validationErrors.phone && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.phone}
                </p>
              )}
              {showPhoneOtpBox && !otpVerified.phone && renderOtpBoxes("phone")}
            </div>

            <div className="w-full space-y-2 relative">
              <Label htmlFor="secondary_mobile_no">
                Alternate Mobile Number <span style={{ color: "red" }}>*</span>
              </Label>
              <div className="flex space-x-2 relative">
                <Input
                  id="secondary_mobile_no"
                  type="tel"
                  value={formData.secondary_mobile_no}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value))
                      setFormData({ ...formData, secondary_mobile_no: value });
                  }}
                  onBlur={() => handleBlur("secondary_mobile_no")}
                  className={`w-full pr-10 border px-3 py-2 ${
                    validationErrors.secondary_mobile_no
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
              </div>
              {validationErrors.secondary_mobile_no && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.secondary_mobile_no}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap lg:flex-nowrap justify-between gap-4">
            {/* Password */}
            <div className="w-full space-y-2">
              <Label htmlFor="password">
                Password <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (
                      (e.ctrlKey || e.metaKey) &&
                      ["v", "c", "x"].includes(e.key.toLowerCase())
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const newPass = e.target.value;
                    setFormData({ ...formData, password: newPass });
                    setPasswordPolicy(validatePasswordPolicy(newPass));
                  }}
                  onBlur={() => handleBlur("password")}
                  className={`pr-10 ${
                    validationErrors.password
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>

              <ul className="mt-2 text-sm space-y-1">
                <li
                  className={
                    passwordPolicy.length ? "text-green-600" : "text-gray-500"
                  }
                >
                  {passwordPolicy.length ? "✅" : "❌"} At least 8 characters
                </li>
                <li
                  className={
                    passwordPolicy.uppercase
                      ? "text-green-600"
                      : "text-gray-500"
                  }
                >
                  {passwordPolicy.uppercase ? "✅" : "❌"} One uppercase letter
                  (A–Z)
                </li>
                <li
                  className={
                    passwordPolicy.lowercase
                      ? "text-green-600"
                      : "text-gray-500"
                  }
                >
                  {passwordPolicy.lowercase ? "✅" : "❌"} One lowercase letter
                  (a–z)
                </li>
                <li
                  className={
                    passwordPolicy.number ? "text-green-600" : "text-gray-500"
                  }
                >
                  {passwordPolicy.number ? "✅" : "❌"} One number (0–9)
                </li>
                <li
                  className={
                    passwordPolicy.special ? "text-green-600" : "text-gray-500"
                  }
                >
                  {passwordPolicy.special ? "✅" : "❌"} One special character
                  (!@#$%^&*)
                </li>
              </ul>

              {validationErrors.password && (
                <p className="text-red-500 text-sm">
                  {validationErrors.password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="w-full space-y-2">
              <Label htmlFor="password_confirmation">
                Confirm Password <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="password_confirmation"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.password_confirmation}
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  onKeyDown={(e) => {
                    if (
                      (e.ctrlKey || e.metaKey) &&
                      ["v", "c", "x"].includes(e.key.toLowerCase())
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      password_confirmation: e.target.value,
                    })
                  }
                  onBlur={() => handleBlur("password_confirmation")}
                  className={`pr-10 ${
                    validationErrors.password_confirmation
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
              {validationErrors.password_confirmation && (
                <p className="text-red-500 text-sm">
                  {validationErrors.password_confirmation}
                </p>
              )}
            </div>
          </div>

          {/* Organization Type */}
          <div className="space-y-2">
            <Label htmlFor="organization_type">
              Organization Type <span className="text-red-500">*</span>
            </Label>
            <select
              id="organization_type"
              value={formData.organization_type}
              onChange={(e) =>
                setFormData({ ...formData, organization_type: e.target.value })
              }
              onBlur={() => handleBlur("organization_type")}
              className={`w-full border rounded px-3 py-2 ${
                validationErrors.organization_type
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            >
              <option value="" disabled>
                Select organization type
              </option>
              <option value="Individual">Individual</option>
              <option value="Master Weaver">Master Weaver</option>
              <option value="Private Firm / Partnership">
                Private Firm / Partnership
              </option>
              <option value="Proprietor">
                Proprietor
              </option>
              <option value="Member of Co-operative Society">
                Member of Co-operative Society
              </option>
              <option value="others">Any Other (Specify)</option>
            </select>
            {validationErrors.organization_type && (
              <p className="text-red-500 text-sm">
                {validationErrors.organization_type}
              </p>
            )}
          </div>

          {formData?.organization_type == "others" && (
            <div className="space-y-2">
              <Label htmlFor="other_organization">
                Other Organization Type<span className="text-red-500">*</span>
              </Label>
              <div className="flex space-x-2">
                <Input
                  id="other_organization"
                  type="text"
                  value={formData.other_organization}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      other_organization: e.target.value,
                    });
                  }}
                  onBlur={() => handleBlur("other_organization")}
                  className={
                    validationErrors.other_organization ? "border-red-500" : ""
                  }
                />
              </div>
              {validationErrors.other_organization && (
                <p className="text-red-500 text-sm">
                  {validationErrors.other_organization}
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <Button
            type="button"
            className="w-full"
            disabled={loading || !otpVerified.phone}
            onClick={() => setOpenModal(true)}
          >
            {loading ? "Creating account..." : "Register"}
          </Button>
        </div>
        <Dialog open={openModal} onOpenChange={setOpenModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>⚠️ Important Notice</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              *If the application is found to be not true, it will be summarily
              rejected at any point of time.
            </div>
            <DialogFooter className="flex space-x-2">
              <Button variant="destructive" onClick={() => setOpenModal(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setOpenModal(false);
                  handleSubmit(new Event("submit"));
                }}
              >
                Agree and Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </form>

      <div className="text-center text-sm">
        Already have an account?{" "}
        <Link to="/login" className="text-primary underline">
          Login
        </Link>
      </div>
    </div>
  );
};

export default RegisterForm;
